import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { useOnClickOutside } from "Utils/hooks";
import { getSemanticMaps, getCurrentSemanticMap } from "Slices/semanticMaps";
import { setCurrentSemanticMapId } from "Slices/appState";
import { resetMissionsQueue } from "Slices/missionPlanner";
import { storageKeys } from "Utils/localStorageUtils";

import {
  StationSelectionWrapperStyled,
  StationSelectorDropdownStyled,
  PlaceholderStyled,
  DropdownContentStyled,
  DropdownItemStyled,
} from "./styles";

const SelectSemanticMap = memo(() => {
  const semanticMaps = useSelector(getSemanticMaps);
  const currentSemanticMap = useSelector(getCurrentSemanticMap);
  const dispatch = useDispatch();
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
  const handleClickOutside = useCallback(() => setShowDropdown(false), []);
  useOnClickOutside(ref, handleClickOutside);

  const isPickerEnabled = semanticMaps.length > 1;

  useEffect(() => {
    if (isPickerEnabled) return;

    setShowDropdown(false);
  }, [isPickerEnabled]);

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const selectedSemanticMapName = useMemo(() => {
    if (currentSemanticMap === undefined) {
      return "...";
    } else {
      return currentSemanticMap?.name;
    }
  }, [currentSemanticMap]);

  const handleSelectedSemanticMap = (semanticMapId: string | null) => {
    dispatch(resetMissionsQueue());
    localStorage.setItem(
      storageKeys.CURRENT_SEMANTIC_MAP_ID,
      JSON.stringify(semanticMapId)
    );
    dispatch(setCurrentSemanticMapId(semanticMapId));
    setShowDropdown(false);
  };

  return (
    <StationSelectionWrapperStyled
      ref={ref}
      isDisabled={!isPickerEnabled}
      hasSelection={!!selectedSemanticMapName}
    >
      <StationSelectorDropdownStyled
        onClick={() => {
          setShowDropdown(!showDropdown);
        }}
        active={showDropdown}
      >
        <PlaceholderStyled>Change zone &gt;</PlaceholderStyled>
      </StationSelectorDropdownStyled>
      {isPickerEnabled && showDropdown && (
        <DropdownContentStyled isSemanticMap={true}>
          {semanticMaps.map((semanticMap) => (
            <DropdownItemStyled
              onClick={() => handleSelectedSemanticMap(semanticMap.id)}
              key={semanticMap.id}
            >
              {semanticMap.name}
            </DropdownItemStyled>
          ))}
        </DropdownContentStyled>
      )}
    </StationSelectionWrapperStyled>
  );
});

export default SelectSemanticMap;
